import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectSelection } from './ProjectSelection';
import { Project, ProjectType, Service } from '../../../types';
import * as Constants from '../../../utils/constants';
import { ServiceRepository } from '../../../repositories';

const DEFAULT_PROJECT_TAB = 'my-projects';

const tabs = [
  { id: 'my-projects', text: 'My Projects', projectType: 'all' },
  { id: 'favorite-projects', text: 'Favorites', projectType: 'favorites' },
  { id: 'recent-projects', text: 'Recent', projectType: 'mrus' },
  { id: 'searched', text: 'Search', projectType: 'searched' },
];

export interface ProjectSelectionContainerProps {
  tabConfigured: boolean;
  tabId: string;
  project: Project;
  favorites: Project[];
  mrus: Project[];
  all: Project[];
  searched: Project[];
  setProject: (project: Project) => any;
  setProjectsTab: (tabId: string) => any;
  setSearchPhrase: (searchPhrase: string) => any;
  setRunSearch: (runSearch: boolean) => any;
  filterProjects: (search: { phrase: string; projectType: ProjectType }) => any;
  loadProjects: () => Promise<void>;
  loadServices: () => Promise<void>;
  setService: (service?: Service) => any;
  resetConnection: () => any;
}

export const ProjectSelectionContainer = ({
  tabConfigured,
  tabId = DEFAULT_PROJECT_TAB,
  project,
  favorites,
  mrus,
  all,
  searched,
  setProject,
  setProjectsTab,
  setSearchPhrase,
  setRunSearch,
  filterProjects,
  loadProjects,
  loadServices,
  setService,
  resetConnection,
}: ProjectSelectionContainerProps) => {
  const [loading, setLoading] = useState(true);
  const [currentFilter, setCurrentFilter] = useState('');
  const [currentSearch, setCurrentSearch] = useState('');

  useEffect(() => {
    loadProjects()
      .then(() => {
        setProjectsTab(tabId ? tabId : DEFAULT_PROJECT_TAB);
        setLoading(false);
      })
      .catch(error => console.error(error));
  }, []);

  const onChange = useCallback(
    (input: string) => {
      if (tabId === 'searched') {
        return;
      }
      setCurrentFilter(input);
      const tab = tabs.find(t => t.id === tabId);
      if (tab?.id) {
        filterProjects({
          phrase: input ?? '',
          projectType: tab.projectType as ProjectType,
        });
      }
    },
    [filterProjects, tabId]
  );

  useEffect(() => {
    onChange(currentFilter);
  }, [currentFilter, onChange, tabId]);

  const onSearch = async (input: string) => {
    if (tabId === 'searched') {
      if (input) {
        setLoading(true);
        setRunSearch(true);
        setSearchPhrase(input);
        setCurrentSearch(input);
        await loadProjects();
        setRunSearch(false);
        setLoading(false);
        return;
      } else {
        await onClear();
        return;
      }
    }
    setCurrentFilter(input);
    const selectedTab = tabs.find(tab => tab.id === tabId);
    if (selectedTab?.id) {
      filterProjects({
        phrase: input ?? '',
        projectType: selectedTab.projectType as ProjectType,
      });
    }
  };

  const onClear = async () => {
    if (tabId === 'searched') {
      setRunSearch(true);
      setCurrentSearch('');
    } else {
      setCurrentFilter('');
    }
    setSearchPhrase('');
    await loadProjects();
    setRunSearch(false);
  };

  const getProjectSelectionItems = useCallback(
    (
      tabId: string,
      favorites: Project[],
      mrus: Project[],
      all: Project[],
      searched: Project[]
    ) => {
      let projects: Project[];
      switch (tabId) {
        case 'favorite-projects':
          projects = favorites;
          break;
        case 'recent-projects':
          projects = mrus;
          break;
        case 'my-projects':
          projects = all;
          break;
        case 'searched':
          projects = searched;
          break;
        default:
          projects = [];
          break;
      }

      return projects
        ? projects
            .map(project => ({
              text: project.name,
              value: project.id,
              isPw365Project:
                project.solutionType === Constants.SolutionTypes.projectWise365,
              additionalText: project.number,
              external: project.isExternal,
              onClick: async () => {
                setService(undefined);
                resetConnection();
                setProject(project);
                if (
                  project.solutionType ===
                  Constants.SolutionTypes.projectWise365
                ) {
                  await loadServices();
                  const service = await ServiceRepository.getServices(
                    'ProjectWise365'
                  );
                  await setService(service[0]);
                }
              },
            }))
            .sort((a, b) =>
              a.text && b.text && a.text.toLowerCase() > b.text.toLowerCase()
                ? 1
                : -1
            )
        : [];
    },
    [loadServices, resetConnection, setProject, setService]
  );

  const projectSelectionItems = useMemo(
    () => getProjectSelectionItems(tabId, favorites, mrus, all, searched),
    [all, favorites, getProjectSelectionItems, mrus, searched, tabId]
  );

  return (
    <ProjectSelection
      tabId={tabId}
      project={project}
      tabConfigured={tabConfigured}
      selectTab={setProjectsTab}
      loading={loading}
      projectSelectionItems={projectSelectionItems}
      onSearch={async (input: string) => onSearch(input)}
      onChange={async (input: string) => onChange(input)}
      onClear={async () => onClear()}
      searchValue={currentSearch}
      filterValue={currentFilter}
      tabs={tabs}
    />
  );
};
